import { api, throwApiError } from "../../../api/api";
import type { LivenessSession } from "../../../components/face-liveness/FaceLivenessCheck";

/**
 * Quiosque da loja (ADR 0031) — telas do DP: aparelhos, cadastro do rosto e
 * conferência das marcações feitas pelo CPF. Só ADMIN e DP.
 */

const auth = (token: string) => {
  const raw = token?.startsWith("Bearer ") ? token.slice(7) : token;
  return { headers: { Authorization: `Bearer ${raw}` } };
};

const call = async <T,>(fallback: string, fn: () => Promise<{ data: T }>): Promise<T> => {
  try {
    return (await fn()).data;
  } catch (error) {
    throwApiError(error, fallback);
    throw error;
  }
};

const blob = async (fallback: string, url: string, token: string): Promise<Blob> => {
  try {
    const { data } = await api.get(url, { ...auth(token), responseType: "blob" });
    return data;
  } catch (error) {
    throwApiError(error, fallback);
    throw error;
  }
};

// ── Aparelhos ───────────────────────────────────────────────────────────────

export type KioskDeviceStatus = "ATIVO" | "AGUARDANDO_ATIVACAO" | "REVOGADO";

export type KioskDevice = {
  id: string;
  name: string;
  store: { id: string; name: string };
  status: KioskDeviceStatus;
  activationPending: boolean;
  activatedAt: string | null;
  lastSeenAt: string | null;
  revokedAt: string | null;
  revokedReason: string | null;
  createdAt: string;
};

export const KIOSK_STATUS_LABELS: Record<KioskDeviceStatus, string> = {
  ATIVO: "Ativo",
  AGUARDANDO_ATIVACAO: "Aguardando ativação",
  REVOGADO: "Revogado",
};

export type ActivationCode = { id: string; activationCode: string; expiresInMinutes: number };

export const listKioskDevicesApi = (token: string) => call<KioskDevice[]>("Erro ao carregar os aparelhos.", () => api.get("/dp/kiosk-devices", auth(token)));

export const createKioskDeviceApi = (token: string, body: { storeId: string; name: string }) =>
  call<ActivationCode & { name: string }>("Erro ao cadastrar o aparelho.", () => api.post("/dp/kiosk-devices", body, auth(token)));

export const newActivationCodeApi = (token: string, id: string) =>
  call<ActivationCode>("Erro ao gerar o código.", () => api.post(`/dp/kiosk-devices/${id}/activation-code`, {}, auth(token)));

export const revokeKioskDeviceApi = (token: string, id: string, reason: string) =>
  call("Erro ao revogar o aparelho.", () => api.post(`/dp/kiosk-devices/${id}/revoke`, { reason }, auth(token)));

// ── Cadastro do rosto ───────────────────────────────────────────────────────

export type FaceEnrollmentState = {
  recognitionAvailable: boolean;
  enrollment: { id: string; enrolledAt: string; livenessConfidence: number; enrolledBy: { id: string; name: string } | null } | null;
};

export const getFaceEnrollmentApi = (token: string, employeeId: string) =>
  call<FaceEnrollmentState>("Erro ao carregar o cadastro do rosto.", () => api.get(`/dp/employees/${employeeId}/face-enrollment`, auth(token)));

export const startFaceEnrollmentApi = (token: string, employeeId: string) =>
  call<LivenessSession>("Erro ao abrir a verificação.", () => api.post(`/dp/employees/${employeeId}/face-enrollment/liveness-sessions`, {}, auth(token)));

export const enrollFaceApi = (token: string, employeeId: string, sessionId: string) =>
  call<FaceEnrollmentState>("Erro ao cadastrar o rosto.", () => api.post(`/dp/employees/${employeeId}/face-enrollment`, { sessionId }, auth(token)));

export const removeFaceEnrollmentApi = (token: string, employeeId: string, reason: string) =>
  call("Erro ao remover o rosto.", () => api.delete(`/dp/employees/${employeeId}/face-enrollment`, { ...auth(token), data: { reason } }));

export const fetchFaceEnrollmentPhotoApi = (token: string, employeeId: string) =>
  blob("Erro ao abrir a foto do cadastro.", `/dp/employees/${employeeId}/face-enrollment/photo`, token);

// ── Conferência das marcações pelo CPF ──────────────────────────────────────

export type ReviewVerdict = "OK" | "SUSPEITA";

export type FlaggedPunch = {
  id: string;
  nsr: number;
  punchedAt: string;
  day: string;
  time: string;
  contractId: string;
  employee: { id: string; name: string; hasFaceEnrollment: boolean };
  store: { id: string; name: string | null } | null;
  device: string | null;
  reason: string;
  livenessConfidence: number | null;
  review: { verdict: ReviewVerdict; note: string | null; reviewedAt: string; reviewedBy: string | null } | null;
};

export const listFlaggedPunchesApi = (token: string, params: { from: string; to: string; storeId?: string; reviewed?: boolean }) =>
  call<FlaggedPunch[]>("Erro ao carregar as marcações.", () => api.get("/dp/time-punches/flagged", { ...auth(token), params }));

export const reviewPunchApi = (token: string, punchId: string, body: { verdict: ReviewVerdict; note?: string }) =>
  call("Erro ao registrar a conferência.", () => api.post(`/dp/time-punches/${punchId}/review`, body, auth(token)));
